/**
 * Dataset Creation Wizard - Main orchestration page
 *
 * Multi-step wizard for creating datasets without file upload.
 * Follows the same pattern as SchemaCreate wizard.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/useToast';
import { useDatasetCreationStore, useDatasetWizardNav } from '@/features/datasets/store/datasetCreationStore';
import { validateDatasetCreation } from '@/features/datasets/utils/datasetValidation';
import { DatasetBasicInfoStep } from '@/features/datasets/components/DatasetBasicInfoStep';
import { DatasetConfigurationStep } from '@/features/datasets/components/DatasetConfigurationStep';
import { DatasetPartitionsStep } from '@/features/datasets/components/DatasetPartitionsStep';
import { DatasetMetadataStep } from '@/features/datasets/components/DatasetMetadataStep';
import { DatasetReviewStep } from '@/features/datasets/components/DatasetReviewStep';

const TOTAL_STEPS = 5;

const STEP_TITLES = [
  'Basic Information',
  'Configuration',
  'Partitions',
  'Metadata',
  'Review & Create',
];

/**
 * Dataset Create Page Component
 */
export const DatasetCreate: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentStep, nextStep, previousStep } = useDatasetWizardNav();
  const store = useDatasetCreationStore();

  /**
   * Handle cancel - navigate back to datasets list
   */
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      store.reset();
      navigate('/datasets');
    }
  };

  /**
   * Validate current step before proceeding
   */
  const canProceedToNextStep = (): boolean => {
    switch (currentStep) {
      case 1: {
        // Basic info: name and location are required
        return store.name.trim().length > 0 && store.name.length <= 255;
      }
      case 2: {
        // Configuration: no required fields (compression has default, source/schema are optional)
        return true;
      }
      case 3: {
        // Partitions: at least 1 required
        return store.partitions.length > 0 && store.partitions.every(p => p.name.trim().length > 0);
      }
      case 4: {
        // Metadata: optional, but if present must be valid
        return store.metadata.every(m => m.key.trim().length > 0 && m.value.trim().length > 0);
      }
      case 5: {
        // Review: validate everything
        const validation = validateDatasetCreation({
          name: store.name,
          sourceId: store.sourceId,
          partitions: store.partitions,
          metadata: store.metadata,
        });
        return validation.isValid;
      }
      default:
        return false;
    }
  };

  /**
   * Handle next step
   */
  const handleNext = () => {
    if (!canProceedToNextStep()) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all required fields before proceeding.',
        variant: 'destructive',
      });
      return;
    }
    nextStep();
  };

  /**
   * Handle previous step
   */
  const handlePrevious = () => {
    previousStep();
  };

  /**
   * Render current step component
   */
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <DatasetBasicInfoStep />;
      case 2:
        return <DatasetConfigurationStep />;
      case 3:
        return <DatasetPartitionsStep />;
      case 4:
        return <DatasetMetadataStep />;
      case 5:
        return <DatasetReviewStep />;
      default:
        return null;
    }
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Create New Dataset</h1>
          <p className="text-muted-foreground mt-1">
            Step {currentStep} of {TOTAL_STEPS}: {STEP_TITLES[currentStep - 1]}
          </p>
        </div>
        <Button variant="ghost" onClick={handleCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between mt-2 text-sm text-muted-foreground">
          {STEP_TITLES.map((title, index) => (
            <div
              key={index}
              className={`${
                index + 1 === currentStep
                  ? 'text-primary font-semibold'
                  : index + 1 < currentStep
                  ? 'text-foreground'
                  : ''
              }`}
            >
              {index + 1}. {title}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card className="p-6 mb-6">
        {renderStep()}
      </Card>

      {/* Navigation - Hide on final step (Review has its own button) */}
      {currentStep < TOTAL_STEPS && (
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceedToNextStep()}
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Previous button only on final step */}
      {currentStep === TOTAL_STEPS && (
        <div className="flex justify-start">
          <Button
            variant="outline"
            onClick={handlePrevious}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
        </div>
      )}
    </div>
  );
};
